import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { fetchThreadContext } from "../../api/apiClient";
import { categoryColor } from "./categoryColor";

const T = {
  ink: "var(--color-ink)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  surface3: "var(--color-surface-press-light)",
};

const CERT_TAG = {
  ok: { label: "Current", bg: "var(--color-success-bg)", fg: "var(--color-success)" },
  warn: { label: "Expiring", bg: "var(--color-warn-bg)", fg: "var(--color-warn)" },
  bad: { label: "Expired", bg: "var(--color-danger-bg)", fg: "var(--color-danger)" },
};

const eyebrowSx = { fontSize: "10px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.ink4 };

function Section({ children, center }) {
  return (
    <Box sx={{
      p: center ? "24px 20px" : "18px 20px", borderBottom: `1px solid ${T.line}`,
      display: "grid", gap: "8px", justifyItems: center ? "center" : "start", textAlign: center ? "center" : "left",
    }}>
      {children}
    </Box>
  );
}

function GoLink({ to, children }) {
  return (
    <Box component={RouterLink} to={to} sx={{
      height: 32, px: "12px", borderRadius: "var(--radius-md)", border: `1px solid ${T.line}`, bgcolor: "#fff",
      fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", display: "inline-flex",
      alignItems: "center", color: T.ink, textDecoration: "none", "&:hover": { bgcolor: T.surface3 },
    }}>
      {children}
    </Box>
  );
}

function Kpi({ value, label }) {
  return (
    <Box sx={{ display: "grid", p: "10px", borderRadius: "var(--radius-md)", bgcolor: "#fff", border: `1px solid ${T.line}` }}>
      <Box sx={{ fontSize: 18, fontWeight: 600, fontFamily: "var(--font-display)" }}>{value}</Box>
      <Box sx={{ fontSize: 11.5, color: T.ink3 }}>{label}</Box>
    </Box>
  );
}

function ClassSummary({ ctx, isStaff }) {
  const to = isStaff ? `/course-hub/${ctx.classId}` : `/student-hub/${ctx.classId}`;
  return (
    <Section>
      <Box sx={eyebrowSx}>Class</Box>
      <Box sx={{ fontSize: 14, fontWeight: 600 }}>{ctx.title}</Box>
      {ctx.code && (
        <Box sx={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: T.ink3, display: "flex", alignItems: "center", gap: "6px" }}>
          <Box component="i" data-testid="context-category-dot" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: categoryColor(ctx.category) }} />
          {ctx.code}
        </Box>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", width: "100%", my: "4px" }}>
        <Kpi value={ctx.members ?? 0} label="Members" />
        <Kpi value={ctx.nextSessionDay || "—"} label="Next session" />
      </Box>
      {ctx.nextSession && <Box sx={{ fontSize: 12.5, color: T.ink3 }}>{ctx.nextSession}</Box>}
      {ctx.classId && <GoLink to={to}>Open class</GoLink>}
    </Section>
  );
}

function StudentRecord({ ctx, Avatar, RoleBadge }) {
  const firstClass = ctx.classes?.[0];
  return (
    <>
      <Section center>
        <Avatar name={ctx.name} role="student" size={56} />
        <Box sx={{ fontSize: 16, fontWeight: 600 }}>{ctx.name}</Box>
        <RoleBadge role="student" />
      </Section>
      <Section>
        <Box sx={eyebrowSx}>In your classes</Box>
        {(ctx.classes || []).map((c) => (
          <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", fontSize: 13 }}>
            <Box component="i" sx={{ width: 8, height: 8, borderRadius: "50%", flex: "none", bgcolor: categoryColor(c.category) }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>{c.title}</Box>
            <Box sx={{ fontFamily: "var(--font-mono)", fontSize: 11.5, opacity: 0.6 }}>{c.code}</Box>
          </Box>
        ))}
      </Section>
      <Section>
        <Box sx={eyebrowSx}>Attendance</Box>
        <Box sx={{ fontSize: 14, fontWeight: 600 }}>{ctx.attendance}</Box>
      </Section>
      <Section>
        <Box sx={eyebrowSx}>Next session</Box>
        <Box sx={{ fontSize: 14, fontWeight: 600 }}>{ctx.nextSession || "None scheduled"}</Box>
      </Section>
      <Section>
        <Box sx={eyebrowSx}>Certifications</Box>
        {(ctx.certifications || []).length === 0 && <Box sx={{ fontSize: 13, color: T.ink3 }}>None on file</Box>}
        {(ctx.certifications || []).map((c) => {
          const tag = CERT_TAG[c.status] || CERT_TAG.ok;
          return (
            <Box key={`${c.name}-${c.detail}`} sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", fontSize: 13 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {c.name}
                <Box sx={{ fontSize: 11.5, opacity: 0.6 }}>{c.detail}</Box>
              </Box>
              <Box component="span" sx={{
                fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
                px: "7px", py: "3px", borderRadius: "var(--radius-xs)", bgcolor: tag.bg, color: tag.fg,
              }}>
                {tag.label}
              </Box>
            </Box>
          );
        })}
      </Section>
      {firstClass && (
        <Section>
          <GoLink to={`/instructor/courses/${firstClass.id}/students/${ctx.studentId}`}>View student record</GoLink>
        </Section>
      )}
    </>
  );
}

function PeerSummary({ ctx, Avatar, RoleBadge }) {
  return (
    <>
      <Section center>
        <Avatar name={ctx.name} role={ctx.role} size={56} />
        <Box sx={{ fontSize: 16, fontWeight: 600 }}>{ctx.name}</Box>
        <RoleBadge role={ctx.role} />
      </Section>
      {ctx.role === "coordinator" && (
        <Section>
          <Box sx={eyebrowSx}>Coordinator</Box>
          <Box sx={{ fontSize: 12.5, color: T.ink3 }}>Scheduling, rooms and enrollments</Box>
        </Section>
      )}
    </>
  );
}

/**
 * Details panel for the active thread. Falls back to `fallback` (the participant list) while the
 * context loads, when it fails, and for group threads.
 */
export default function ThreadContextPanel({ thread, isStaff, Avatar, RoleBadge, fallback }) {
  const [ctx, setCtx] = useState(null);
  const threadId = thread?.id;

  useEffect(() => {
    setCtx(null);
    if (!threadId || typeof fetchThreadContext !== "function") return undefined;
    let cancelled = false;
    Promise.resolve(fetchThreadContext(threadId))
      .then((data) => { if (!cancelled) setCtx(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [threadId]);

  if (!ctx) return fallback;
  if (ctx.type === "class") {
    return (
      <>
        <ClassSummary ctx={ctx} isStaff={isStaff} />
        {fallback}
      </>
    );
  }
  if (ctx.type === "student") return <StudentRecord ctx={ctx} Avatar={Avatar} RoleBadge={RoleBadge} />;
  if (ctx.type === "staff" || ctx.type === "peer") return <PeerSummary ctx={ctx} Avatar={Avatar} RoleBadge={RoleBadge} />;
  return fallback;
}
